<template>
  <div class="slider-wrapper" :class="{ inline: props.displayValuesInline }">
    <SliderRoot
      v-model="sliderValue"
      :min="min"
      :max="max"
      :step="step"
      class="radix-slider"
      @valueCommit="handleChange"
      @update:modelValue="handleChange"
      :id="props.id"
      :aria-label="props.ariaLabel"
    >
      <SliderTrack class="radix-slider-track">
        <SliderRange class="radix-slider-range" />
      </SliderTrack>
      <SliderThumb
        v-for="(val, idx) in sliderValue"
        :key="idx"
        class="radix-slider-thumb"
        :aria-labelledby="props.labelId"
        :aria-label="props.ariaLabel || undefined"
      />
    </SliderRoot>
    <div class="slider-values">
      <span>{{ displayValue(sliderValue[0], min, 'Ei alarajaa') }}</span>
      <template v-if="sliderValue[1]"
        >-
        <span>{{ displayValue(sliderValue[1], max, 'Ei ylärajaa') }}</span>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { SliderRoot, SliderTrack, SliderRange, SliderThumb } from 'radix-vue';
import utils from '@/app/utils/utils';
import { debounce } from 'lodash-es';

export interface SliderConf {
  start: number[];
  step: number;
  range: { min: number; max: number };
  unit?: string;
}

const emit = defineEmits<{
  (e: 'changed', value: number[]): void;
}>();

const props = defineProps<{
  id: string;
  conf: SliderConf;
  hideLowerLimitString?: boolean;
  showImmediateValues?: boolean;
  displayValuesInline?: boolean;
  labelId?: string;
  splitThousands?: boolean;
  displayDecimals?: boolean;
  ariaLabel?: string;
}>();

const min = computed(() => props.conf.range.min);
const max = computed(() => props.conf.range.max);
const step = computed(() => props.conf.step);
const unit = computed(() => props.conf.unit);

const sliderValue = ref([...props.conf.start]);

watch(
  () => props.conf,
  (newConf) => {
    sliderValue.value = [...newConf.start];
  },
  { deep: true }
);

const debouncedEmit = debounce((val: number[]) => emit('changed', val), 300);

function handleChange(val: number[] | undefined) {
  if (!val) {
    return;
  }
  if (props.showImmediateValues) {
    emit('changed', val);
  } else {
    debouncedEmit(val);
  }
}

function displayValue(val: number, edge: number, label: string) {
  if (props.hideLowerLimitString && val === edge) {
    return `${val} ${unit.value || ''}`;
  } else if (val === edge) {
    return label;
  }

  let displayVal: string;

  if (props.splitThousands) {
    displayVal = utils.formatNumberWithSpaces(Math.round(val));
  } else if (props.displayDecimals) {
    displayVal = utils.formatNumber(val, 2);
  } else {
    displayVal = val.toString();
  }

  return `${displayVal} ${unit.value || ''}`;
}
</script>
